import React, { useState, useEffect } from "react";
import { Button } from "../../../@/components/ui/button";
import { MAX_LIVE_STREAM_TITLE_LENGTH } from "../../../lib/constants";
import { formatDateTime } from "../../../lib/utils";
import { DateTimePicker } from "./DateAndTimePicker";
import { toast } from "../../../@/components/ui/use-toast";

interface StreamSchedulerProps {
  title: string;
  selectedDateTime: string;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDateTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSchedule: () => void;
  isRescheduling?: boolean;
}

const StreamScheduler: React.FC<StreamSchedulerProps> = ({
  title,
  selectedDateTime,
  onTitleChange,
  onDateTimeChange,
  onSchedule,
  isRescheduling = false,
}) => {
  const [isValidDateTime, setIsValidDateTime] = useState(true);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    if (selectedDateTime) {
      const selectedDate = new Date(selectedDateTime);
      const now = new Date();

      if (selectedDate <= now) {
        setIsValidDateTime(false);
        toast({
          title: "Please select a future date and time",
          description: "The selected date and time must be in the future.",
          variant: "destructive",
          showIcon: true,
        });
      } else {
        setIsValidDateTime(true);
      }
    }
  }, [selectedDateTime]);

  const openDatePicker = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker();
  };

  return (
    <div className="bg-white p-6 rounded-lg transition-all duration-200 hover:shadow-md space-y-4">
      <div className="w-full max-w-md">
        <label htmlFor="title" className="text-gray-600 text-sm">
          Stream Title
          <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          placeholder="Enter stream title"
          value={title}
          onChange={onTitleChange}
          maxLength={MAX_LIVE_STREAM_TITLE_LENGTH}
          className="w-full h-10 p-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-customGreenColor/50 focus:border-customGreenColor"
        />
        <span className="text-xs text-gray-500">
          {title.length}/{MAX_LIVE_STREAM_TITLE_LENGTH}
        </span>
      </div>

      <div className="w-full max-w-md">
        <label htmlFor="startDateTime" className="text-gray-600 text-sm">
          Start Date and Time
          <span className="text-red-500">*</span>
        </label>
        <DateTimePicker
          value={selectedDateTime ? new Date(selectedDateTime) : undefined}
          onValueChange={(date: Date | undefined) => {
            const syntheticEvent = {
              target: {
                value: date?.toISOString() || "",
              },
            } as React.ChangeEvent<HTMLInputElement>;
            onDateTimeChange(syntheticEvent);
          }}
        />
      </div>

      {selectedDateTime && (
        <p
          className={`text-sm ${
            isValidDateTime ? "text-gray-500" : "text-red-500"
          }`}
        >
          Your stream will start on{" "}
          {formatDateTime(selectedDateTime, "dddd, MMMM D, YYYY [at] h:mm A")}
        </p>
      )}

      <div>
        <Button
          className={`w-full max-w-md px-8 py-3 rounded-lg text-white font-medium transition-all duration-200
            ${
              selectedDateTime && isValidDateTime && title
                ? "bg-customGreenColor hover:bg-customGreenColor/90 hover:shadow-lg"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          onClick={onSchedule}
          disabled={!selectedDateTime || !title || !isValidDateTime}
        >
          {isRescheduling ? "Reschedule Live Stream" : "Schedule Live Stream"}
        </Button>
      </div>
    </div>
  );
};

export default StreamScheduler;
